// Strict input mask for date fields: the value is always (part of) TT.MM.JJJJ or MM/JJJJ. Digits typed in the
// middle overwrite the digit there instead of being inserted, extra digits are dropped, separators come by
// themselves and impossible days/months (33, 13) are refused. Pure functions, tested in mask.test.ts.

export type DateKind = 'day' | 'month';

// Case-insensitive: the Personnel design writes „jj.mm.aaaa“.
const DAY_PH = /^(TT\.MM\.JJJJ|JJ\.MM\.AAAA|DD\.MM\.YYYY)$/i, MONTH_PH = /^(MM\/JJJJ|MM\/AAAA|MM\/YYYY)$/i;
/** Which kind of date an input holds: `data-date="day|month"`, or the design's placeholder (DE or translated FR). */
export function dateKindOf(el: EventTarget | null): DateKind | null {
  if (!(el instanceof HTMLInputElement) || el.readOnly || el.disabled || (el.type && el.type !== 'text')) return null;
  const k = el.dataset.date;
  if (k === 'day' || k === 'month') return k;
  const ph = (el.getAttribute('placeholder') || '').trim();
  return DAY_PH.test(ph) ? 'day' : MONTH_PH.test(ph) ? 'month' : null;
}

const SPEC = {
  day: { sep: '.', ends: [2, 4, 8], len: 10 }, // digit index where each group ends
  month: { sep: '/', ends: [2, 6], len: 7 },
} as const;
export const maxLength = (kind: DateKind) => SPEC[kind].len;

/** Digits → "30.09.2026"; `trailing` adds the separator after a just completed group ("30."). */
export function formatDigits(kind: DateKind, d: string, trailing = false): string {
  const { sep, ends } = SPEC[kind];
  let out = '', from = 0;
  for (let g = 0; g < ends.length && from < d.length; g++) {
    const part = d.slice(from, ends[g]);
    out += part;
    from = ends[g]!;
    if (from < d.length || (trailing && part.length === ends[g]! - (g ? ends[g - 1]! : 0) && g < ends.length - 1)) out += sep;
  }
  return out;
}
const digitsBefore = (v: string, pos: number) => v.slice(0, pos).replace(/\D/g, '').length;
/** Caret position after the `n`-th digit (past a following separator, so typing goes on in the next group). */
function caretAt(v: string, n: number): number {
  let i = 0, c = 0;
  while (i < v.length && c < n) { if (/\d/.test(v[i]!)) c++; i++; }
  while (i < v.length && !/\d/.test(v[i]!)) i++;
  return i;
}
/** Two-digit groups must be a possible day (01–31) / month (01–12). */
function validGroups(kind: DateKind, d: string): boolean {
  const ok = (s: string, max: number) => s.length < 2 || (+s >= 1 && +s <= max);
  return kind === 'day' ? ok(d.slice(0, 2), 31) && ok(d.slice(2, 4), 12) : ok(d.slice(0, 2), 12);
}

export interface Edit { type: string; data?: string | null }
/** Applies one keystroke/paste to the field. `null` = leave the browser's default (nothing to do for us). */
export function applyEdit(kind: DateKind, v: string, selStart: number, selEnd: number, e: Edit): { value: string; caret: number } | null {
  const max = SPEC[kind].ends.at(-1)!;
  let d = v.replace(/\D/g, '').slice(0, max);
  const s = Math.min(digitsBefore(v, selStart), d.length), en = Math.min(digitsBefore(v, selEnd), d.length);
  let c = s;
  if (e.type.startsWith('insert')) {
    const data = e.data ?? '';
    // A whole date pasted/dropped („30.09.2026“, „1.9.26“) replaces the field.
    const whole = parseDate(kind, data);
    if (whole && data.length > 2) {
      const value = kind === 'day' ? fmtDay(whole.y, whole.m, whole.d!) : fmtMonth(whole.y, whole.m);
      return { value, caret: value.length };
    }
    if (en > s) d = d.slice(0, s) + d.slice(en);
    for (const ch of data) {
      if (/\d/.test(ch)) {
        const start = [0, ...SPEC[kind].ends].filter((x) => x <= c).at(-1)!; // first digit of the group the caret is in
        let next = d, nc = c;
        // „4“ as first day digit can only mean 04 (month: 2–9 → 02–09).
        const firstOfDay = kind === 'day' && c === 0, firstOfMonth = (kind === 'day' && c === 2) || (kind === 'month' && c === 0);
        if (c === start && c === d.length && ((firstOfDay && ch > '3') || (firstOfMonth && ch > '1'))) { next = d + '0'; nc = c + 1; }
        if (nc < next.length) next = next.slice(0, nc) + ch + next.slice(nc + 1); // overwrite
        else if (next.length < max) next += ch;
        else break; // field full
        if (!validGroups(kind, next)) break;
        d = next; c = nc + 1;
      } else if (/[./\-, ]/.test(ch) && c === d.length) {
        // „1.“ → „01.“: a separator closes a one-digit day/month group.
        const ends = SPEC[kind].ends, g = ends.findIndex((x) => c < x), start = g > 0 ? ends[g - 1]! : 0;
        if (g >= 0 && g < ends.length - 1 && c - start === 1) { const n = d.slice(0, start) + '0' + d.slice(start); if (validGroups(kind, n)) { d = n; c++; } }
      }
    }
  } else if (e.type === 'deleteContentBackward') {
    if (en > s) d = d.slice(0, s) + d.slice(en);
    else if (s > 0) { d = d.slice(0, s - 1) + d.slice(s); c = s - 1; }
  } else if (e.type === 'deleteContentForward' || e.type === 'deleteByCut' || e.type === 'deleteByDrag') {
    d = en > s ? d.slice(0, s) + d.slice(en) : d.slice(0, s) + d.slice(s + 1);
  } else if (e.type.startsWith('delete') && e.type.includes('Backward')) {
    d = d.slice(Math.max(en, s)); c = 0; // ⌥⌫ / ⌘⌫
  } else if (e.type.startsWith('delete')) {
    d = d.slice(0, s);
  } else return null;
  const value = formatDigits(kind, d, c === d.length && e.type.startsWith('insert'));
  return { value, caret: caretAt(value, c) };
}

/** "30.09.2026" / "30.9.26" → {y, m, d}; month kind "09/2026" → {y, m}. */
export function parseDate(kind: DateKind, v: string): { y: number; m: number; d?: number } | null {
  if (kind === 'month') {
    const x = /^(\d{1,2})[./-](\d{4})$/.exec(v.trim());
    return x && +x[1]! >= 1 && +x[1]! <= 12 ? { y: +x[2]!, m: +x[1]! } : null;
  }
  const x = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(v.trim());
  if (!x) return null;
  const y = x[3]!.length === 2 ? 2000 + +x[3]! : +x[3]!, m = +x[2]!, d = +x[1]!;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? { y, m, d } : null;
}

export const fmtDay = (y: number, m: number, d: number) => String(d).padStart(2, '0') + '.' + String(m).padStart(2, '0') + '.' + y;
export const fmtMonth = (y: number, m: number) => String(m).padStart(2, '0') + '/' + y;
